import { postWidgetImpression } from 'api/targeting'
import { isNil, omit } from 'ramda'
import { CampaignReenrollmentType } from 'types/campaigns'
import { ConversationId } from 'types/conversation'
import { WidgetImpressionSource } from 'types/targeting'
import { warn } from 'utils'
import { sessionStore } from 'utils/storage'

import { createImpressionEvent } from './creators'

const ALWAYS_SEND_IDS_SESSION_STORE_KEY = 'ALWAYS_SEND_IDS'

export const isPlaybookImpressionEvent = (source: WidgetImpressionSource) => source === WidgetImpressionSource.ACTIVE_PLAYBOOK || source === WidgetImpressionSource.PASSIVE_PLAYBOOK

export const getImpressionSendId = async (
  playbookId: number,
  endUserId: number,
  sessionId: number,
  instanceId: number,
  activeConversationId: ConversationId,
  campaignReenrollmentType: CampaignReenrollmentType,
  virtual: boolean): Promise<string> => {
  if (!virtual) {
    return !!activeConversationId ? `CONVERSATION:${activeConversationId}` : undefined
  }

  let sendId
  if (!endUserId || !playbookId || !sessionId) {
    return sendId
  }

  switch (campaignReenrollmentType) {
    case CampaignReenrollmentType.ONCE:
      sendId = `ONCE:${endUserId}:${playbookId}`
      break
    case CampaignReenrollmentType.ONCE_PER_SESSION:
      sendId = `ONCE_PER_SESSION:${endUserId}:${playbookId}:${sessionId}`
      break
    case CampaignReenrollmentType.ALWAYS:
      const sendIdFromStore = await getSendIdInStore(playbookId)

      if (!!sendIdFromStore) {
        return sendIdFromStore
      }

      sendId = `ALWAYS:${endUserId}:${playbookId}:${instanceId}`
      addSendIdToStore(playbookId, sendId)
      break
  }

  return sendId
}

export const trackWidgetImpression = async (source: WidgetImpressionSource, meta: Record<string, any> = {}): Promise<boolean> => {
  const impressionEvent = await createImpressionEvent({
    source,
    meta
  })

  if (impressionEvent) {
    try {
      postWidgetImpression(impressionEvent)
      return true
    } catch(err) {
      warn(['Failed to send widget impression', err])
    }
  }

  return false
}


const addSendIdToStore = async (playbookId: number, sendId: string) => {
  const { data: currentStore = {} } = await sessionStore.get(ALWAYS_SEND_IDS_SESSION_STORE_KEY)
  await sessionStore.set(ALWAYS_SEND_IDS_SESSION_STORE_KEY, {
    ...currentStore,
    [playbookId]: sendId
  })
}

export const removeSendIdFromStore = async (playbookId: number) => {
  const { data: storedSendIds } = await sessionStore.get(ALWAYS_SEND_IDS_SESSION_STORE_KEY)

  const storeWithRemovedSendId = isNil(storedSendIds) ? {} : omit([`${playbookId}`], storedSendIds)
  await sessionStore.set(ALWAYS_SEND_IDS_SESSION_STORE_KEY, storeWithRemovedSendId)
}

const getSendIdInStore = async (playbookId: number) => {
  const { data: storedSendIds } = await sessionStore.get(ALWAYS_SEND_IDS_SESSION_STORE_KEY)
  return isNil(storedSendIds) ? null : storedSendIds[playbookId]
}
